import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BarChart3, ChevronRight } from 'lucide-react'

interface CompSetCardProps {
  hotelId: string
}

function fmtUsd(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return `$${n.toFixed(0)}`
}

export default function CompSetCard({ hotelId }: CompSetCardProps) {
  const { data: compCount = 0 } = useQuery({
    queryKey: ['comp_properties_count', hotelId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('comp_properties')
        .select('id', { count: 'exact', head: true })
        .eq('hotel_id', hotelId)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!hotelId,
  })

  const { data: rankings, isLoading } = useQuery({
    queryKey: ['comp_rankings_latest', hotelId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comp_rankings')
        .select('date, metric, subject_value, comp_median, rank, comp_count')
        .eq('hotel_id', hotelId)
        .in('metric', ['adr', 'revpar'])
        .order('date', { ascending: false })
        .limit(10)
      if (error) throw error
      return data ?? []
    },
    enabled: !!hotelId && compCount > 0,
  })

  const latestDate = rankings?.[0]?.date
  const adrRow = rankings?.find((r) => r.metric === 'adr' && r.date === latestDate)
  const revparRow = rankings?.find((r) => r.metric === 'revpar' && r.date === latestDate)
  const hasRankings = !!(adrRow?.rank || revparRow?.rank)

  return (
    <Card className="h-full border border-vesta-navy/10 bg-white shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-vesta-navy">
          <BarChart3 className="h-4 w-4 text-vesta-gold" />
          Comp set ranking
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {compCount === 0 ? (
          <p className="text-sm text-vesta-navy/80">
            Add your comp set to see how you rank vs nearby properties on ADR and RevPAR.
          </p>
        ) : isLoading ? (
          <div className="h-16 animate-pulse rounded-lg bg-vesta-mist/50" />
        ) : !hasRankings ? (
          <p className="text-sm text-vesta-navy/80">
            {compCount} comp{compCount !== 1 ? 's' : ''} on file — import comp daily metrics to compute rankings.
          </p>
        ) : (
          <div className="space-y-3">
            {latestDate ? (
              <p className="text-xs text-vesta-navy/65">Latest data: {latestDate}</p>
            ) : null}
            {adrRow?.rank != null && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-vesta-navy/85">ADR</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums text-vesta-navy">
                    {fmtUsd(adrRow.subject_value)} vs {fmtUsd(adrRow.comp_median)} median
                  </span>
                  <Badge className="border-vesta-gold/30 bg-vesta-gold/15 text-vesta-navy">
                    {adrRow.rank} of {(adrRow.comp_count ?? 0) + 1}
                  </Badge>
                </div>
              </div>
            )}
            {revparRow?.rank != null && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-vesta-navy/85">RevPAR</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold tabular-nums text-vesta-navy">
                    {fmtUsd(revparRow.subject_value)} vs {fmtUsd(revparRow.comp_median)} median
                  </span>
                  <Badge className="border-vesta-gold/30 bg-vesta-gold/15 text-vesta-navy">
                    {revparRow.rank} of {(revparRow.comp_count ?? 0) + 1}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}
        <Button
          asChild
          variant="outline"
          size="sm"
          className="w-full border-vesta-navy/20 text-vesta-navy hover:border-vesta-gold/40"
        >
          <Link to="/comp-set">
            Manage comp set
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
